import React, { useId } from "react"

// Pallino: il puntino della "i" di inCampo, diventato pallina. È la mascotte
// del sito (docs/sito.md): di fronte, senza braccia né gambe, solo i piedi,
// con la fascetta bassa sulla fronte. Due colori e un'ombra, niente
// contorni, come nelle illustrazioni a cui si ispira.
//
// I cugini sono le palline degli altri sport: il padel, verde lime con la
// stessa cucitura, e il pickleball, giallo e coi buchi. Si vedono quando chi
// visita il sito sceglie quello sport.
//
// Quattro pose: fermo, felice (salta, a occhi chiusi e bocca aperta), dorme
// e storto: gli occhi che guardano in basso, di lato, e la bocca a onda, per
// quando nell'app qualcosa si è inceppato (src/crash/CrashGuard.js).
// Il disegno sta in un quadrato di 150: tutto il resto è la sua scala.

const KINDS = {
	tennis: { seams: true, holes: false, band: true },
	padel: { seams: true, holes: false, band: false },
	pickleball: { seams: false, holes: true, band: false },
}

const EYES = {
	fermo: "open",
	felice: "happy",
	dorme: "sleep",
	storto: "open",
}

// Dove guardano le pupille: avanti, o storto in basso, una per parte
const PUPILS = {
	fermo: [
		[64.5, 76],
		[88.5, 76],
	],
	storto: [
		[62, 78],
		[90, 78],
	],
}

const MOUTH = {
	fermo: "M68 89 Q75 95 82 89",
	felice: "M66 88 Q75 102 84 88 Z",
	dorme: "M71 91 Q75 93.5 79 91",
	storto: "M66 96 Q71 90 76 94 T 85 92",
}

function Pallino({ pose = "fermo", kind = "tennis", size = 120, title, className, style }) {
	const id = useId().replace(/:/g, "")
	const shape = KINDS[kind] ?? KINDS.tennis
	const color = name => `var(--c-ball-${KINDS[kind] ? kind : "tennis"}${name})`
	const isHappy = pose === "felice"
	const isAsleep = pose === "dorme"

	return (
		<svg
			viewBox="0 0 150 150"
			width={size}
			height={size}
			className={className}
			style={{ overflow: "visible", display: "block", ...style }}
			role={title ? "img" : undefined}
			aria-label={title}
			aria-hidden={title ? undefined : true}>
			<defs>
				<clipPath id={`body-${id}`}>
					<circle cx="75" cy="76" r="44" />
				</clipPath>
				{/* l'ombra sul fianco: tutta la pallina meno un cerchio spostato
				    verso la luce, che arriva da in alto a sinistra */}
				<mask id={`side-${id}`} maskUnits="userSpaceOnUse" x="-20" y="-20" width="190" height="190">
					<rect x="-20" y="-20" width="190" height="190" fill="#fff" />
					<circle cx="64" cy="63" r="45" fill="#000" />
				</mask>
			</defs>

			<ellipse
				cx="75"
				cy="127"
				rx={isHappy ? 26 : 38}
				ry="5.5"
				fill="var(--c-scene-shade)"
				fillOpacity={isHappy ? 0.18 : 0.3}
			/>

			<g transform={isHappy ? "translate(0,-14)" : undefined}>
				<g
					transform={
						isAsleep
							? "translate(72 126) scale(1.05 0.94) translate(-72 -126)"
							: undefined
					}>
					<ellipse cx="61" cy="121" rx="11" ry="5.6" fill={color("-deep")} />
					<ellipse cx="89" cy="121.5" rx="11.5" ry="5.8" fill={color("-deep")} />

					<circle cx="75" cy="76" r="44" fill={color("")} />
					<circle cx="75" cy="76" r="44" fill={color("-side")} mask={`url(#side-${id})`} />

					<g clipPath={`url(#body-${id})`}>
						{shape.seams && (
							<g stroke={color("-seam")} strokeWidth="3.6" strokeLinecap="round" fill="none">
								<path d="M33 40 C 58 60, 58 94, 34 114" />
								<path d="M112 32 C 88 52, 88 92, 114 110" />
							</g>
						)}
						{shape.holes && (
							<g fill={color("-seam")}>
								<circle cx="44" cy="58" r="4.2" />
								<circle cx="56" cy="42" r="3.6" />
								<circle cx="38" cy="78" r="3.8" />
								<circle cx="74" cy="38" r="3.2" />
								<circle cx="104" cy="50" r="3.6" />
								<circle cx="112" cy="88" r="3.4" />
							</g>
						)}
						{shape.band && (
							<g fill="none">
								<path d="M29 58 Q75 32 121 58" stroke="var(--c-ball-band)" strokeWidth="10" />
								<path
									d="M29 58 Q75 32 121 58"
									stroke="var(--c-ball-band-stripe)"
									strokeWidth="2.4"
								/>
							</g>
						)}
					</g>

					{EYES[pose] === "open" && (
						<g>
							<ellipse cx="63" cy="74" rx="8.5" ry="10" fill="var(--c-white)" />
							<ellipse cx="87" cy="74" rx="8.5" ry="10" fill="var(--c-white)" />
							{(PUPILS[pose] ?? PUPILS.fermo).map(([cx, cy]) => (
								<circle key={cx} cx={cx} cy={cy} r="5.2" fill="var(--c-ball-eye)" />
							))}
						</g>
					)}
					{EYES[pose] === "happy" && (
						<path
							d="M55 76 Q63 66 71 76 M79 76 Q87 66 95 76"
							stroke="var(--c-ball-eye)"
							strokeWidth="3.2"
							strokeLinecap="round"
							fill="none"
						/>
					)}
					{EYES[pose] === "sleep" && (
						<path
							d="M55 75 Q63 81 71 75 M79 75 Q87 81 95 75"
							stroke="var(--c-ball-eye)"
							strokeWidth="3"
							strokeLinecap="round"
							fill="none"
						/>
					)}
					<path
						d={MOUTH[pose] ?? MOUTH.fermo}
						stroke="var(--c-ball-eye)"
						strokeWidth="2.8"
						strokeLinecap="round"
						strokeLinejoin="round"
						fill={isHappy ? "var(--c-ball-eye)" : "none"}
					/>
				</g>

				{isAsleep && (
					<g fontFamily="Outfit, Poppins, sans-serif" fontWeight="700" fill={color("-deep")}>
						<text x="112" y="32" fontSize="16">
							z
						</text>
						<text x="124" y="18" fontSize="11" fillOpacity="0.7">
							z
						</text>
					</g>
				)}
			</g>
		</svg>
	)
}

export default Pallino
